import { Sparkles, RefreshCw, DollarSign, Check, Plus, X } from "lucide-react";
import { CATEGORIES, CATEGORY_LABELS } from "@/lib/shoppingConstants";

/**
 * AIShoppingTab — the "AI Handleliste" tab on the Shopping List page.
 *
 * Shows AI-generated suggestions grouped by category, the Kassal store-
 * comparison panel, and the average/Rema-1000 total cost banner.
 */
export default function AIShoppingTab({
  shoppingList,
  isHousehold,
  generatingAI,
  estimatingPrices,
  storeComparison,
  selectedCompareStore,
  setSelectedCompareStore,
  onGenerateAIList,
  onEstimatePrices,
  onFetchLivePrices,
  onMoveAIToMain,
  onAddAIItemToMain,
  onRemoveAIItem,
}) {
  const aiItems = shoppingList?.ai_items || [];
  return (
    <div data-testid="ai-shopping-list">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4 p-4 bg-gradient-to-br from-orange-50 to-amber-50 border border-orange-100 rounded-xl">
        <div className="flex-1">
          <h3 className="text-sm font-semibold text-orange-900 flex items-center gap-2" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "1.1rem" }}>
            <Sparkles className="h-4 w-4" /> AI-foreslåtte varer
          </h3>
          <p className="text-xs text-orange-800/80 mt-1">
            Basert på matplanen for uken, skalert til {isHousehold ? "personer hjemme" : "personer om bord"}, minus det du allerede har i beholdningen.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={onGenerateAIList}
            disabled={generatingAI}
            className="inline-flex items-center gap-1.5 rounded-md bg-orange-600 text-white px-3 py-2 text-xs font-semibold hover:bg-orange-700 disabled:opacity-50 transition-colors"
            data-testid="generate-ai-list-btn"
          >
            {generatingAI ? <><RefreshCw className="h-3.5 w-3.5 animate-spin" /> Genererer...</> : <><Sparkles className="h-3.5 w-3.5" /> Generer fra matplan</>}
          </button>
          {aiItems.length > 0 && (
            <>
              <button
                onClick={onEstimatePrices}
                disabled={estimatingPrices}
                className="inline-flex items-center gap-1.5 rounded-md bg-white border border-orange-300 text-orange-700 px-3 py-2 text-xs font-semibold hover:bg-orange-50 disabled:opacity-50 transition-colors"
                data-testid="estimate-prices-btn"
              >
                {estimatingPrices ? <><RefreshCw className="h-3.5 w-3.5 animate-spin" /> Regner...</> : <><DollarSign className="h-3.5 w-3.5" /> Estimer (AI)</>}
              </button>
              <button
                onClick={onFetchLivePrices}
                disabled={estimatingPrices}
                className="inline-flex items-center gap-1.5 rounded-md bg-gradient-to-r from-emerald-600 to-teal-600 text-white px-3 py-2 text-xs font-semibold hover:from-emerald-700 hover:to-teal-700 disabled:opacity-50 transition-colors"
                data-testid="live-prices-btn"
                title="Hent sanntidspriser fra kassal.app (Coop, Meny m.fl.)"
              >
                {estimatingPrices ? <><RefreshCw className="h-3.5 w-3.5 animate-spin" /> Henter...</> : <><DollarSign className="h-3.5 w-3.5" /> Live priser</>}
              </button>
              <button
                onClick={onMoveAIToMain}
                className="inline-flex items-center gap-1.5 rounded-md bg-white border border-orange-300 text-orange-700 px-3 py-2 text-xs font-semibold hover:bg-orange-50 transition-colors"
                data-testid="move-all-ai-btn"
              >
                <Check className="h-3.5 w-3.5" /> Flytt alle til hovedliste
              </button>
            </>
          )}
        </div>
      </div>

      {aiItems.length === 0 ? (
        <div className="text-center py-12 bg-white border border-stone-100 rounded-xl" data-testid="empty-ai-list">
          <Sparkles className="h-12 w-12 mx-auto text-orange-200 mb-4" />
          <h3 className="text-lg font-medium text-stone-600" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Ingen AI-forslag ennå</h3>
          <p className="text-sm text-stone-400 mt-1">Klikk "Generer fra matplan" for å se hva du mangler basert på ukens retter.</p>
        </div>
      ) : (
        <>
          {/* Butikk-sammenligning: per-butikk totaler (Kassal) */}
          {storeComparison.stores.length > 0 && (
            <StoreComparison
              storeComparison={storeComparison}
              selectedCompareStore={selectedCompareStore}
              setSelectedCompareStore={setSelectedCompareStore}
            />
          )}
          {/* Total cost banner if prices are estimated */}
          <AITotalBanner aiItems={aiItems} />
          <AIItemsGroupedList
            aiItems={aiItems}
            onAddAIItemToMain={onAddAIItemToMain}
            onRemoveAIItem={onRemoveAIItem}
          />
        </>
      )}
    </div>
  );
}

function StoreComparison({ storeComparison, selectedCompareStore, setSelectedCompareStore }) {
  return (
    <div className="mb-3 bg-white border border-teal-200 rounded-xl p-4" data-testid="store-comparison">
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        <div>
          <h3 className="text-sm font-semibold text-teal-800 inline-flex items-center gap-1.5" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            💡 Butikk-sammenligning
          </h3>
          <p className="text-[11px] text-stone-500">Klikk en butikk for å se priser per vare. Sortert billigst → dyrest.</p>
        </div>
        <span className="text-[10px] uppercase tracking-wider text-stone-400 font-semibold">Kilde: kassal.app</span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
        {storeComparison.stores.map((s, idx) => {
          const active = selectedCompareStore === s.store_code;
          const isCheapest = idx === 0;
          return (
            <button
              key={s.store_code}
              onClick={() => setSelectedCompareStore(active ? null : s.store_code)}
              className={`text-left rounded-lg border px-3 py-2.5 transition-all ${active ? "bg-teal-50 border-teal-400 ring-2 ring-teal-200" : "bg-stone-50 border-stone-200 hover:border-teal-300 hover:bg-white"}`}
              data-testid={`compare-store-${s.store_code}`}
            >
              <div className="flex items-center justify-between gap-1 mb-0.5">
                <span className="text-xs font-semibold text-stone-800 truncate">{s.store_name}</span>
                {isCheapest && <span className="text-[9px] font-bold bg-emerald-600 text-white px-1.5 py-0.5 rounded-full uppercase tracking-wider">Billigst</span>}
              </div>
              <div className="text-xl font-bold text-stone-900" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                {Math.round(s.total_nok)} <span className="text-xs font-normal text-stone-500">kr</span>
              </div>
              <div className="text-[10px] text-stone-500">
                {s.items_matched}/{storeComparison.items_total} varer · {s.coverage_pct}% dekning
              </div>
            </button>
          );
        })}
      </div>
      {selectedCompareStore && (() => {
        const s = storeComparison.stores.find((x) => x.store_code === selectedCompareStore);
        if (!s) return null;
        return (
          <div className="mt-3 pt-3 border-t border-stone-100" data-testid="compare-detail">
            <p className="text-[11px] text-stone-500 mb-2">
              <span className="font-semibold text-stone-700">{s.store_name}</span> – {s.items_matched} varer matchet
              {s.items_matched < storeComparison.items_total && (
                <span className="ml-1 text-amber-600">· {storeComparison.items_total - s.items_matched} mangler</span>
              )}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 max-h-64 overflow-y-auto">
              {s.items.map((it) => (
                <div key={it.item_id || `${s.store_code}-${it.item_name}`} className="flex items-center justify-between gap-2 bg-stone-50 rounded px-2 py-1 text-xs">
                  <span className="flex-1 min-w-0">
                    <span className="font-medium text-stone-700 block truncate">{it.item_name}</span>
                    <span className="text-[10px] text-stone-400 truncate block">{it.product_name}</span>
                  </span>
                  <span className="font-bold text-teal-700 flex-shrink-0">{Math.round(it.price_nok)} kr</span>
                </div>
              ))}
            </div>
          </div>
        );
      })()}
    </div>
  );
}

function AITotalBanner({ aiItems }) {
  const priced = aiItems.filter((i) => typeof i.price_nok === "number");
  if (priced.length === 0) return null;
  const totalAvg = priced.reduce((s, i) => s + (i.price_nok || 0), 0);
  const totalRema = aiItems.reduce((s, i) => s + (i.price_rema_nok || 0), 0);
  const saved = totalAvg - totalRema;
  return (
    <div className="mb-3 bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-xl p-4" data-testid="ai-total-banner">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <span className="text-[11px] uppercase tracking-wider text-green-700 font-semibold">Gjennomsnittpris</span>
          <div className="text-2xl font-bold text-green-900 leading-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            {Math.round(totalAvg)} <span className="text-base font-normal">kr</span>
          </div>
          <div className="text-[10px] text-green-700/70">Kiwi · Meny · Coop</div>
        </div>
        {totalRema > 0 && (
          <div className="text-right border-l border-green-200 pl-3" data-testid="rema-total">
            <span className="text-[11px] uppercase tracking-wider text-red-700 font-semibold">🛒 Rema 1000</span>
            <div className="text-2xl font-bold text-red-700 leading-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              {Math.round(totalRema)} <span className="text-base font-normal">kr</span>
            </div>
            {saved > 0 && (
              <div className="text-[11px] text-emerald-700 font-semibold">Spar {Math.round(saved)} kr</div>
            )}
          </div>
        )}
      </div>
      <div className="mt-2 text-[11px] text-stone-500">
        {priced.length} av {aiItems.length} varer · AI-estimat
      </div>
    </div>
  );
}

function AIItemsGroupedList({ aiItems, onAddAIItemToMain, onRemoveAIItem }) {
  const grouped = {};
  for (const it of aiItems) {
    const cat = CATEGORIES.includes(it.category) ? it.category : "Other";
    (grouped[cat] ||= []).push(it);
  }
  return (
    <div className="space-y-4" data-testid="ai-items">
      {CATEGORIES.filter((c) => grouped[c]?.length).map((cat) => (
        <div key={cat} data-testid={`ai-cat-${cat}`}>
          <h3 className="text-xs uppercase tracking-[0.2em] text-orange-500 font-semibold mb-2">{CATEGORY_LABELS[cat] || cat}</h3>
          <div className="bg-white border border-orange-100 rounded-xl divide-y divide-orange-50">
            {grouped[cat].map((item) => (
              <div key={item.id} className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 px-3 sm:px-4 py-3" data-testid={`ai-item-${item.id}`}>
                <div className="flex-1 min-w-0">
                  <span className="text-sm font-medium text-stone-800 block truncate">{item.name}</span>
                  {(item.amount || item.unit) && (
                    <span className="text-xs text-stone-400">{item.amount} {item.unit}</span>
                  )}
                  {typeof item.price_nok === "number" && (
                    <span className="text-xs text-green-700 font-semibold ml-2" title="Gjennomsnittpris">~{Math.round(item.price_nok)} kr</span>
                  )}
                  {typeof item.price_rema_nok === "number" && (
                    <span className="text-xs text-red-600 font-semibold ml-1" title="Rema 1000">· 🛒 {Math.round(item.price_rema_nok)} kr</span>
                  )}
                  {typeof item.price_live_nok === "number" && (
                    <span className="text-xs text-teal-700 font-semibold ml-1" title={`Live pris – ${item.price_live_store || "kassal.app"}${item.price_live_product ? " – " + item.price_live_product : ""}`} data-testid={`live-price-${item.id}`}>
                      · 💡 {Math.round(item.price_live_nok)} kr {item.price_live_store && <span className="text-[10px] text-teal-600">({item.price_live_store})</span>}
                    </span>
                  )}
                </div>
                <div className="flex gap-2 flex-shrink-0 self-end sm:self-auto">
                  <button
                    onClick={() => onAddAIItemToMain(item)}
                    className="inline-flex items-center gap-1.5 rounded-md bg-orange-600 text-white px-3 py-1.5 text-xs font-semibold hover:bg-orange-700 transition-colors"
                    data-testid={`add-ai-item-${item.id}`}
                  >
                    <Plus className="h-3 w-3" /> Legg til
                  </button>
                  <button
                    onClick={() => onRemoveAIItem(item.id)}
                    className="p-1.5 rounded-md text-stone-400 hover:bg-red-50 hover:text-red-500 transition-colors"
                    data-testid={`remove-ai-item-${item.id}`}
                    title="Fjern forslag"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
